import { useState } from 'react';
import { Color, fill, Size, formatDateTime, t } from '@bms2/shared';
import { Button } from '../../components/controls';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import styles from './RecordPage.module.css';
import { post, useApi } from './api';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const onCallJobPath = (workOrderId: string): string => `/work/on-call/jobs/${workOrderId}`;
/** The separator between an alert row's three facts, as the board's rows use it. */
const SEPARATOR = ' · ';

export const onCallAcknowledgePath = (workOrderId: string, alertId: string): string => `/work/on-call/jobs/${workOrderId}/alerts/${alertId}/acknowledge`;

interface AlertRow {
  id: string;
  role: 'Primary' | 'Backup';
  name: string;
  sentAt: string;
  textStatus: string | null;
  acknowledgedAt: string | null;
  mine: boolean;
}

export interface OnCallJobAnswer {
  alerts: AlertRow[];
  nobodyOnCall: boolean;
  unacknowledged: boolean;
}

/**
 * 🔒 The job's on-call panel (piece 29; T390, T1730): who an after-hours
 * emergency alerted, when, by text or in the app alone, and who acknowledged;
 * or that no one was on call. 🔒 Opening the page from the alert's link only
 * opens the job; Acknowledge on the row is the press that acknowledges
 * (decision 10173). 🔨 Draws nothing on a job no alert concerns.
 */
export function WorkOrderOnCall({ workOrderId, apiBaseUrl = '' }: { workOrderId: string; apiBaseUrl?: string }) {
  const state = useApi<OnCallJobAnswer>(onCallJobPath(workOrderId), apiBaseUrl);
  const zone = useViewerZone();
  const format = useDateFormat();
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const answer = state.state.kind === 'ready' && state.state.data && Array.isArray(state.state.data.alerts) ? state.state.data : null;

  const acknowledge = async (alertId: string) => {
    const wrote = await post(onCallAcknowledgePath(workOrderId, alertId), {}, apiBaseUrl);
    if (!wrote.ok) return setProblem(wrote.message);
    setProblem(null);
    setDone(true);
    state.reload();
  };

  if (!answer || (answer.alerts.length === 0 && !answer.nobodyOnCall)) return null;
  const at = (instant: string) => formatDateTime(instant, zone, format);
  return (
    <div data-work-order-on-call={workOrderId} data-on-call-unacknowledged={answer.unacknowledged ? 'true' : 'false'}>
      <p className={styles.sectionTitle}>{t('workOrder.onCall.title')}</p>
      {answer.nobodyOnCall ? <p className={styles.problem} data-on-call-nobody="true">{t('workOrder.onCall.nobody')}</p> : null}
      {answer.unacknowledged && !answer.nobodyOnCall ? <p className={styles.problem} data-on-call-waiting="true">{t('workOrder.onCall.unacknowledged')}</p> : null}
      {done ? <p className={styles.hint} data-on-call-acknowledged-notice="true">{t('workOrder.onCall.acknowledgedNotice')}</p> : null}
      {problem ? <p className={styles.problem}>{problem}</p> : null}
      <ul className={styles.plainList}>
        {answer.alerts.map((row) => (
          <li key={row.id} className={styles.hint} data-on-call-alert={row.role} data-on-call-acknowledged={row.acknowledgedAt ? 'true' : 'false'}>
            {[
              fill(t('workOrder.onCall.alerted'), { name: row.name, role: t(row.role === 'Primary' ? 'workOrder.onCall.primary' : 'workOrder.onCall.backup'), at: at(row.sentAt) }),
              row.textStatus === null ? t('workOrder.onCall.inAppOnly') : fill(t('workOrder.onCall.text'), { status: row.textStatus ?? '' }),
              row.acknowledgedAt ? fill(t('workOrder.onCall.acknowledged'), { at: at(row.acknowledgedAt) }) : t('workOrder.onCall.notAcknowledged'),
            ].join(SEPARATOR)}
            {row.mine && row.acknowledgedAt === null ? (
              <>
                {' '}
                <Button size={Size.Small} color={Color.Navy} text={[t('workOrder.onCall.acknowledge')]} actions={[() => void acknowledge(row.id)]} />
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
